import React, {useContext, useEffect, useMemo, useState} from 'react';
import {Button, Card, CardActionArea, CardActions, CardContent, Grid, TextField, Typography} from '@mui/material';
import {GlobalStateContext} from '../../state/GlobalStateProvider';
import {useAppointmentGraphQL} from '../../service/useAppointmentGraphQL';
import {useTenantConfig} from '../../security/tenant-config-context';
import {useTranslation} from 'react-i18next';
import ServiceCard from "../../components/common/ServiceCard";

export default function AppointmentServiceStep({updateCurrentStep}) {
    const {globalState, updateGlobalState} = useContext(GlobalStateContext);
    const {useActiveServices} = useAppointmentGraphQL();
    const {data: servicesData, loading: servicesLoading, refetch} = useActiveServices();
    const {cfg} = useTenantConfig();
    const {t} = useTranslation();
    const [currentStep, setCurrentStep] = useState(2);

    const selectedServiceUuid = globalState.appointment.selectedServiceUuid;
    const notes = globalState.appointment.notes || '';

    const speciesFromTenant = useMemo(() => {
        if (cfg?.animalTypes?.includes('DOG')) return 'DOG';
        if (cfg?.animalTypes?.includes('CAT')) return 'CAT';
        return null;
    }, [cfg]);

    const filteredServices = useMemo(() => {
        const services = servicesData?.services || [];
        return services.filter(s => s.active && (!s.species || s.species === speciesFromTenant));
    }, [servicesData, speciesFromTenant]);

    const selectedService = useMemo(() => filteredServices.find(s => s.uuid === selectedServiceUuid) || null, [filteredServices, selectedServiceUuid]);

    const onSelectService = (service) => {
        updateGlobalState({appointment: {selectedServiceUuid: service.uuid, serviceName: service.name, price: service.price, advance: service.price / 2}});
    };

    const onChangeNotes = (e) => {
        updateGlobalState({appointment: {notes: e.target.value}});
    };

    const goNext = () => {
        setCurrentStep(3); // just to retrigger fetch is user goes back and services are updated
        updateCurrentStep(3);
    };
    const goBack = () => {
        setCurrentStep(1);
        updateCurrentStep(1);
    }

    useEffect(() => {
        if (currentStep === 1) {
            refetch().then();
        }
    }, [currentStep]);

    return (
        <Grid container alignItems="center" justifyContent="center" spacing={1} sx={{textAlign: 'center'}}>
            <Grid item xs={12} sm={10} md={8} lg={6}>
                <Card variant="outlined">
                    <CardContent>
                        {/* Section 2: services */}
                        <Typography variant="subtitle1" fontWeight={600} sx={{mb: 1}}>{t('chooseService')}</Typography>
                        {servicesLoading && <Typography>{t('loadingServices')}</Typography>}
                        {!servicesLoading && filteredServices.length === 0 && (
                            <Typography variant="body2" sx={{p: 2}}>{t('noServicesFound')}</Typography>
                        )}
                        {!servicesLoading && filteredServices.map(s => (
                            <ServiceCard
                                key={s.uuid}
                                service={s}
                                onSelect={onSelectService}
                                selected={selectedServiceUuid === s.uuid}
                                showBuffers={false}
                                data-testid={`service-card-${s.name.replace(/\s+/g, '-').toLowerCase()}`}
                            />
                        ))}

                        {/* Section 5: notes */}
                        {!servicesLoading && filteredServices.length !== 0 && (
                            <>
                                <Typography variant="subtitle1" fontWeight={600} sx={{mt: 2, mb: 1}}>{t('noteOptional')}</Typography>
                                <TextField
                                    fullWidth
                                    multiline
                                    minRows={3}
                                    placeholder={t('notePlaceholder')}
                                    value={notes}
                                    onChange={onChangeNotes}
                                    inputProps={{ "data-testid": "appointment-notes" }}/>
                            </>
                        )}
                    </CardContent>
                    <CardActions style={{marginBottom: '10px', justifyContent: 'space-between'}}>
                        <Button variant="outlined" onClick={goBack} data-testid="previous-button">{t('previous')}</Button>
                        <Button variant="contained" onClick={goNext} disabled={!selectedService} data-testid="next-button">{t('next')}</Button>
                    </CardActions>
                </Card>
            </Grid>
        </Grid>
    );
}
